import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import Logo from '../components/ui/Logo';
import Footer from '../components/organisms/Footer';

const TermsPage = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <motion.header 
        className="bg-white shadow-sm border-b"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-neutral-600 hover:text-neutral-900"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <Logo variant="full" size="medium" className="hidden sm:block" />
            </div>
          </div>
        </div>
      </motion.header>

      <motion.main
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        {/* Welcome and Effective Date */}
        <motion.section variants={itemVariants} className="mb-12">
          <Badge variant="secondary" className="mb-4">Legal Hub</Badge>
          <Card className="bg-white shadow-sm">
            <CardContent className="pt-6">
              <h1 className="text-3xl font-heading font-bold text-neutral-900 mb-4">CROWBLOCK Legal Hub</h1>
              <p className="text-neutral-600 mb-2">Effective Date: 01-August-2025</p>
              <p className="text-lg text-neutral-800">
                Welcome to CROWBLOCK, a crypto-powered crowdfunding platform. By using our services, you agree to the terms, policies, and disclaimers outlined below.
              </p>
            </CardContent>
          </Card>
        </motion.section>

        {/* Terms of Service */}
        <motion.section variants={itemVariants} className="mb-12">
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-semibold text-neutral-900">1. Terms of Service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Eligibility</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>You must be at least 18 years old to use CROWBLOCK.</li>
                  <li>Your use of the platform must comply with the laws of your jurisdiction.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Services Provided</h3>
                <p className="text-neutral-600 mb-2">CROWBLOCK enables users to create, contribute to, and manage crowdfunding campaigns using cryptocurrency.</p>
                <p className="text-neutral-600">A portion of platform income supports global hunger alleviation initiatives.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">User Responsibilities</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>Provide accurate information when creating campaigns or contributing.</li>
                  <li>You are responsible for the legality and fulfillment of your campaign.</li>
                  <li>You may not use the platform for fraudulent, illegal, or harmful activities.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Donations & Contributions</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>Contributions are made in cryptocurrency.</li>
                  <li>Transactions are generally non-refundable, except where required by law.</li>
                  <li>Donors should carefully evaluate campaigns before contributing.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Platform Fees</h3>
                <p className="text-neutral-600 mb-2">CROWBLOCK may charge service fees, disclosed at the time of campaign setup.</p>
                <p className="text-neutral-600">A portion of these fees funds hunger-alleviation programs.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Intellectual Property</h3>
                <p className="text-neutral-600 mb-2">Users retain ownership of content they post but grant CROWBLOCK a license to display it.</p>
                <p className="text-neutral-600">CROWBLOCK branding and software remain our property.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Disclaimer of Warranties</h3>
                <p className="text-neutral-600">The platform is provided "as is" without warranties of any kind.</p>
                <p className="text-neutral-600">We do not guarantee project success, fundraising outcomes, or uninterrupted services.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Limitation of Liability</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>CROWBLOCK is not liable for indirect, incidental, or consequential damages.</li>
                  <li>Our total liability is limited to fees paid by you in the past 12 months.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Termination</h3>
                <p className="text-neutral-600">We may suspend or terminate accounts for violations or fraudulent activity.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Governing Law</h3>
                <p className="text-neutral-600">These Terms are governed by the laws of Nigeria Government.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Changes to Terms</h3>
                <p className="text-neutral-600">We may update these Terms at any time. Continued use of the platform constitutes acceptance.</p>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* Risk Disclaimer */}
        <motion.section variants={itemVariants} className="mb-12">
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-semibold text-neutral-900">3. Risk Disclaimer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="list-disc list-inside space-y-2 text-neutral-600">
                <li><strong>Cryptocurrency Volatility</strong>: Crypto values fluctuate and may impact contributions.</li>
                <li><strong>No Guarantee of Project Success</strong>: Campaigns may not succeed, and outcomes are not guaranteed.</li>
                <li><strong>Irreversible Transactions</strong>: Crypto transactions are typically final and non-refundable.</li>
                <li><strong>Regulatory Uncertainty</strong>: Regulations may vary and affect platform use.</li>
                <li><strong>Security Risks</strong>: Users must secure their wallets and credentials. CROWBLOCK is not liable for stolen keys or hacked accounts.</li>
                <li><strong>Technology Risks</strong>: Platform downtime, cyberattacks, or errors may occur.</li>
                <li><strong>No Financial or Legal Advice</strong>: CROWBLOCK does not provide financial, investment, or legal advice.</li>
              </ul>
              <p className="text-neutral-800 font-medium">By using CROWBLOCK, you acknowledge and accept these risks.</p>
            </CardContent>
          </Card>
        </motion.section>

        {/* Contact Us */}
        <motion.section variants={itemVariants}>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-semibold text-neutral-900">4. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-neutral-600">For any questions about these Terms, Policies, or Disclaimers, please contact us:</p>
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
                <div className="flex items-center space-x-2 text-primary-teal">
                  <Mail className="h-5 w-5" />
                  <span>crowblockofficial@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2 text-primary-teal">
                  <Globe className="h-5 w-5" />
                  <span>https://crowblock.com</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </motion.main>

      <Footer variant="minimal" />
    </div>
  );
};

export default TermsPage;